import React from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FilterIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * Where a piece of content has reached. `all` sends nothing.
 *
 * These map onto the two status columns the server filters on, not onto `stage` — the tutor
 * endpoint has no `stage` param, and lead_status + approval_status express the same chain.
 */
export const STATUS_FILTERS = [
  { value: "all", label: "All", params: {} },
  { value: "awaiting_tutor", label: "With lead tutor", params: { lead_status: "pending" } },
  {
    value: "awaiting_admin",
    label: "Awaiting admin",
    params: { lead_status: "approved", approval_status: "pending" },
  },
  { value: "approved", label: "Approved", params: { approval_status: "approved" } },
  { value: "rejected", label: "Not approved", params: { approval_status: "rejected" } },
];

export const statusParamsFor = (value) =>
  STATUS_FILTERS.find((s) => s.value === value)?.params ?? {};

/**
 * Cascading content filters: group → week / subject → topic → subtopic, plus the approval status.
 *
 * Each select is disabled until its parent is chosen and changing a parent drops everything below
 * it, so the form can never send a subtopic that does not belong to the selected topic. Every
 * value here is a real query parameter on `tutor/resources`, so the filtering happens on the
 * server rather than over a page of already-fetched rows.
 */
export default function TutorContentFilters({
  filters,
  onChange,
  status,
  onStatusChange,
  groups,
  weeks,
  subjects,
  topics,
  subtopics,
  onReset,
  disabled = false,
}) {
  const set = (key) => (e) => onChange(key, e.target.value);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>
            Filter content
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<RefreshIcon />} onClick={onReset} disabled={disabled}>
            Reset
          </Button>
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_, v) => v && onStatusChange(v)}
          sx={{ mb: 2, flexWrap: "wrap", "& .MuiToggleButton-root": { fontWeight: 600 } }}
        >
          {STATUS_FILTERS.map((s) => (
            <ToggleButton key={s.value} value={s.value}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Group"
              value={filters.course_plan_id}
              onChange={set("course_plan_id")}
              disabled={disabled}
            >
              <MenuItem value="">
                <em>— All —</em>
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                  {g.course?.name ? ` — ${g.course.name}` : ""}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Week"
              value={filters.plan_week_id}
              onChange={set("plan_week_id")}
              disabled={disabled || !filters.course_plan_id}
            >
              <MenuItem value="">
                <em>— All —</em>
              </MenuItem>
              {weeks.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  Week {w.week_number}
                  {w.is_current ? " (current)" : ""}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Subject"
              value={filters.subject_id}
              onChange={set("subject_id")}
              disabled={disabled || !filters.course_plan_id}
            >
              <MenuItem value="">
                <em>— All —</em>
              </MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.subject_id} value={s.subject_id}>
                  {s.subject_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Topic"
              value={filters.topic_id}
              onChange={set("topic_id")}
              disabled={disabled || !filters.subject_id}
            >
              <MenuItem value="">
                <em>— All —</em>
              </MenuItem>
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Subtopic"
              value={filters.subtopic_id}
              onChange={set("subtopic_id")}
              disabled={disabled || !filters.topic_id || subtopics.length === 0}
            >
              <MenuItem value="">
                <em>— All —</em>
              </MenuItem>
              {subtopics.map((st) => (
                <MenuItem key={st.id} value={st.id}>
                  {st.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
